/**
 * Markdown Library - Regex Patterns
 * 
 * Centralized regex patterns for content type detection.
 * All patterns are named exports for clarity and reusability.
 */

// =============================================================================
// Brazilian Number Patterns
// =============================================================================

/** Matches Brazilian currency format: R$ 1.234,56 or 1.234,56 */
export const BRAZILIAN_CURRENCY_PATTERN = /^R\$\s*-?\d{1,3}(\.\d{3})*,\d{2}$/;

/** Matches Brazilian number format with thousands separator: 1.234,56 */
export const BRAZILIAN_NUMBER_WITH_THOUSANDS = /^-?\d{1,3}(\.\d{3})*,\d{2}$/;

/** Matches Brazilian decimal number: 123,45 */
export const BRAZILIAN_DECIMAL = /^-?\d+,\d+$/;

/** Matches integer: 12345 */
export const INTEGER_PATTERN = /^-?\d+$/;

// =============================================================================
// Percentage Patterns
// =============================================================================

/** Matches percentage with thousands: 1.234,56% */
export const PERCENTAGE_WITH_THOUSANDS = /^-?\d{1,3}(\.\d{3})*,\d{2}%$/;

/** Matches simple percentage: 12,34% or 12% */
export const SIMPLE_PERCENTAGE = /^-?\d+,?\d*%$/;

// =============================================================================
// Markdown Patterns
// =============================================================================

/** Matches markdown tables */
export const MARKDOWN_TABLE_PATTERN = /(\|[^\n]+\|\n\|[-:\s|]+\|\n(?:\|[^\n]+\|\n?)*)/g;

/** Keywords that indicate a numeric column header */
export const NUMERIC_HEADER_KEYWORDS = [
    'total',
    'valor',
    'preço',
    'contribuição',
    '%',
    'r$',
    'realizado',
    'orçado',
    'quantidade',
    'qtd',
    'soma',
    'média',
    'count',
    'sum',
    'avg',
] as const;

// =============================================================================
// SQL Syntax Highlighting Patterns
// =============================================================================

export const SQL_KEYWORDS = [
    'SELECT', 'FROM', 'WHERE', 'GROUP BY', 'ORDER BY', 'HAVING', 'JOIN', 'LEFT JOIN',
    'RIGHT JOIN', 'INNER JOIN', 'OUTER JOIN', 'ON', 'AS', 'WITH', 'LIMIT', 'OFFSET',
    'DISTINCT', 'UNION', 'UNION ALL'
] as const;

export const SQL_FUNCTIONS = [
    'COUNT', 'SUM', 'AVG', 'MIN', 'MAX', 'COALESCE', 'NULLIF', 'CAST', 'ROUND',
    'DATE_TRUNC', 'TO_CHAR', 'EXTRACT', 'SUBSTRING', 'UPPER', 'LOWER'
] as const;

export const SQL_OPERATORS = [
    'AND', 'OR', 'IN', 'NOT IN', 'EXISTS', 'NOT EXISTS', 'LIKE', 'NOT LIKE',
    'BETWEEN', 'NOT BETWEEN', 'IS NULL', 'IS NOT NULL', 'DESC', 'ASC'
] as const;

export const SQL_CONDITIONALS = [
    'CASE', 'WHEN', 'THEN', 'ELSE', 'END'
] as const;

// =============================================================================
// Helper Functions
// =============================================================================

/**
 * Creates a regex pattern that matches any of the given words as whole words.
 * Handles multi-word patterns (e.g., "GROUP BY") by replacing spaces with \s+.
 */
export function createWordBoundaryPattern(words: readonly string[]): RegExp {
    const escapedWords = words.map(word => word.replace(/\s+/g, '\\s+'));
    const pattern = `\\b(${escapedWords.join('|')})\\b`;
    return new RegExp(pattern, 'gi');
}
